import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchShiftChangeOverForCreate,
  fetchShiftChangeOverById,
  saveShiftChangeOver,
  clearShiftChangeOver,
} from "../../API_Service/ShiftChangeOver_Slice";
import { Dropdown, TextInput, Textarea } from "../../Common/UIComponents";

const iCls =
  "w-full bg-[#f1f4f4] rounded-xl px-4 py-2.5 text-sm font-medium text-[#181c1d] border border-transparent focus:outline-none focus:ring-2 focus:ring-[#08757C] focus:bg-white transition-all";

// Same field-type set the Form Builder config canvas defines
// (ApplicationConfig/ShiftChangeOver/formbuilder.js's FIELD_META) — each
// column's FieldType (from ShiftchangeOverColumn_Mst / FieldType_Mst) picks
// which control renders here, instead of every field being a fixed textarea.
const splitOptions = (options) =>
  (options ?? "")
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean);

function ShiftChangeOverPanel({
  facilityId,
  employeeId,
  companyId,
  shiftChangeOverId = 0,
  readOnly = false,
  onClose,
  onSaved,
  // Optional (message, isError) toast callback — handleSubmit calls
  // onClose() right after a successful save, which unmounts this whole
  // popup, so a toast built from THIS component's own local state would
  // be torn down before it's ever seen. The caller passes its own
  // still-mounted toast instead — same bottom-center pill used on the
  // Facility Booking Gantt's confirm flow.
  showToast,
}) {
  const dispatch = useDispatch();
  const { current, loading, saving } = useSelector((s) => s.shiftChangeOver);
  const [columns, setColumns] = useState([]);
  const [errors, setErrors] = useState({});
  // Redux's `saving` flag lags a click by at least one render — two clicks
  // close enough together (a real double-click, or an impatient second tap
  // while nothing visible has happened yet) can both get through before the
  // button disables, firing two ShiftChangeSave calls that race each other
  // and each create their own ShiftchangeOver_Mst row. A ref updates
  // synchronously, so it closes that gap regardless of render timing.
  const submittingRef = useRef(false);

  useEffect(() => {
    if (shiftChangeOverId > 0) {
      dispatch(fetchShiftChangeOverById({ id: shiftChangeOverId, companyId }));
    } else {
      dispatch(
        fetchShiftChangeOverForCreate({
          shiftChangeOverId: 0,
          facilityId,
          employeeId,
          // Server overwrites this with DateTime.Now (GetShiftChangeOverCreate
          // in ShiftChangeOverController.cs) — toISOString()'s UTC value was
          // making the "already logged this shift?" match against
          // ShiftchangeOver_Mst silently miss. Left populated for readability
          // only.
          savedDate: new Date().toISOString(),
          companyId,
        }),
      );
    }
    return () => dispatch(clearShiftChangeOver());
  }, [shiftChangeOverId, facilityId, employeeId, companyId, dispatch]);

  useEffect(() => {
    if (current?.ColumnDataList) setColumns(current.ColumnDataList);
  }, [current]);

  const updateValue = (colId, value) => {
    setColumns((prev) =>
      prev.map((c) =>
        c.ShiftchangeOverColumn_ID === colId
          ? { ...c, ShiftchangeOver_Value: value }
          : c,
      ),
    );
    setErrors((e) => ({ ...e, [colId]: undefined }));
  };

  const regularColumns = columns.filter(
    (c) => c.ShiftchangeOverColumn_Name !== "Alternate Project",
  );
  const altProjectColumn = columns.find(
    (c) => c.ShiftchangeOverColumn_Name === "Alternate Project",
  );

  const handleSubmit = async () => {
    // Blocks re-entrant clicks synchronously — see submittingRef above.
    if (submittingRef.current) return;
    submittingRef.current = true;

    try {
      // Same "required unless explicitly false" rule the label's asterisk
      // uses — blocks the save until every required field is filled, and
      // surfaces all of them at once (banner + per-field message) instead
      // of stopping at the first miss.
      const missingErrors = {};
      columns.forEach((c) => {
        if (c.IsRequired !== false && !c.ShiftchangeOver_Value) {
          missingErrors[c.ShiftchangeOverColumn_ID] =
            `${c.ShiftchangeOverColumn_Name} is required`;
        }
      });
      setErrors(missingErrors);
      if (Object.keys(missingErrors).length > 0) {
        return;
      }

      const wasUpdate = (current?.ShiftchangeOver_ID ?? 0) > 0;
      const result = await dispatch(
        saveShiftChangeOver({
          ShiftchangeOver_ID: current?.ShiftchangeOver_ID ?? 0,
          FacilityID: facilityId,
          CompanyId: companyId,
          CreatedBy: employeeId,
          
          CreatedDate: new Date().toISOString(),
          ColumnDataList: columns,
        }),
      );
      if (saveShiftChangeOver.fulfilled.match(result)) {
        showToast?.(
          wasUpdate
            ? "Shift change over updated successfully."
            : "Shift change over saved successfully.",
        );
        onSaved?.();
        onClose();
      } else {
        showToast?.("Couldn't save the shift change over. Please try again.", true);
      }
    } finally {
      submittingRef.current = false;
    }
  };

  
  const renderFieldControl = (col, disabled) => {
    const value = col.ShiftchangeOver_Value || "";
    const options = splitOptions(col.Options);

    switch (col.FieldType) {
      case "Text":
        return (
          <TextInput
            id={`col-${col.ShiftchangeOverColumn_ID}`}
            value={value}
            onChange={(e) =>
              updateValue(col.ShiftchangeOverColumn_ID, e.target.value)
            }
            disabled={disabled}
          />
        );

      case "DatePicker":
        return (
          <input
            type="date"
            className={`${iCls} ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
            value={value}
            onChange={(e) =>
              updateValue(col.ShiftchangeOverColumn_ID, e.target.value)
            }
            disabled={disabled}
          />
        );

      case "FileUpload":
        return (
          <input
            type="file"
            className={`${iCls} py-2 ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
            onChange={(e) =>
              updateValue(
                col.ShiftchangeOverColumn_ID,
                e.target.files?.[0]?.name ?? "",
              )
            }
            disabled={disabled}
          />
        );

      case "Dropdown":
        return (
          <Dropdown
            options={options.map((o) => ({ value: o, label: o }))}
            value={value}
            onChange={(v) => updateValue(col.ShiftchangeOverColumn_ID, v)}
            placeholder={`Select ${col.ShiftchangeOverColumn_Name}`}
            disabled={disabled}
          />
        );

      case "AutoCompleteDropdown":
        return (
          <Dropdown
            options={options.map((o) => ({ value: o, label: o }))}
            value={value}
            onChange={(v) => updateValue(col.ShiftchangeOverColumn_ID, v)}
            placeholder={`Search ${col.ShiftchangeOverColumn_Name}`}
            searchPlaceholder="Type to search…"
            disabled={disabled}
          />
        );

      case "MultiDropdown":
        return (
          <Dropdown
            multi
            options={options.map((o) => ({ value: o, label: o }))}
            value={value ? value.split(",") : []}
            onChange={(vals) =>
              updateValue(col.ShiftchangeOverColumn_ID, (vals ?? []).join(","))
            }
            placeholder={`Select ${col.ShiftchangeOverColumn_Name}`}
            disabled={disabled}
          />
        );

      case "CheckBox": {
        const selected = value ? value.split(",") : [];
        return (
          <div className="flex flex-wrap gap-3 pt-1">
            {options.map((o) => (
              <label
                key={o}
                className={`flex items-center gap-2 text-sm font-medium text-[#181c1d] ${disabled ? "opacity-70" : "cursor-pointer"}`}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selected, o]
                      : selected.filter((s) => s !== o);
                    updateValue(col.ShiftchangeOverColumn_ID, next.join(","));
                  }}
                  style={{ width: "16px", height: "16px", accentColor: "#08757C" }}
                />
                {o}
              </label>
            ))}
          </div>
        );
      }

      case "Radio":
        return (
          <div className="flex flex-wrap gap-4 pt-1">
            {options.map((o) => (
              <label
                key={o}
                className={`flex items-center gap-2 text-sm font-medium text-[#181c1d] ${disabled ? "opacity-70" : "cursor-pointer"}`}
              >
                <input
                  type="radio"
                  name={`col-${col.ShiftchangeOverColumn_ID}`}
                  checked={value === o}
                  disabled={disabled}
                  onChange={() => updateValue(col.ShiftchangeOverColumn_ID, o)}
                  style={{ width: "16px", height: "16px", accentColor: "#08757C" }}
                />
                {o}
              </label>
            ))}
          </div>
        );

      case "Textarea":
      default:
        return (
          <Textarea
            id={`col-${col.ShiftchangeOverColumn_ID}`}
            value={value}
            onChange={(e) =>
              updateValue(col.ShiftchangeOverColumn_ID, e.target.value)
            }
            rows={3}
            maxLength={3500}
            disabled={disabled}
          />
        );
    }
  };

  // Same condition ShiftChangeSave branches on server-side (ShiftchangeOver_ID
  // > 0 → UpdateShiftChange) — drives both the header label and the button
  // so "Save" only shows for a genuinely new record.
  const isUpdate = (current?.ShiftchangeOver_ID ?? 0) > 0;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[3px] z-[200]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
        <div
          className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
          style={{ animation: "fadeUp 0.25s cubic-bezier(0.4,0,0.2,1)" }}
        >
          <style>{`@keyframes fadeUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

          {/* Header */}
          <div
            className="px-7 py-5 flex items-center justify-between flex-shrink-0"
            style={{ background: "linear-gradient(135deg,#08757C,#0a9aa3)" }}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="material-symbols-outlined text-white text-[22px]">
                  swap_horiz
                </span>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest mb-0.5">
                  {readOnly ? "View" : isUpdate ? "Edit" : "New"}
                </p>
                <h3
                  className="text-base font-black text-white"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Shift Change Over
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="h-9 w-9 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors flex-shrink-0"
            >
              <span className="material-symbols-outlined text-[20px]">
                close
              </span>
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-7 py-6 space-y-4">
            {loading || !current ? (
              <div className="flex items-center justify-center py-12 text-[#9eabac]">
                <span className="material-symbols-outlined text-[28px] animate-spin mr-2">
                  progress_activity
                </span>
                <span className="text-sm">Loading...</span>
              </div>
            ) : (
              <>
                {regularColumns.map((col) => (
                  <div key={col.ShiftchangeOverColumn_ID}>
                    <label className="block text-[10px] font-bold text-[#08757C] uppercase tracking-widest mb-1.5">
                      {col.ShiftchangeOverColumn_Name}{" "}
                      {col.IsRequired !== false && (
                        <span className="text-red-400">*</span>
                      )}
                    </label>
                    {renderFieldControl(col, readOnly)}
                    {errors[col.ShiftchangeOverColumn_ID] && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors[col.ShiftchangeOverColumn_ID]}
                      </p>
                    )}
                  </div>
                ))}

                {altProjectColumn && (
                  <div>
                    <label className="block text-[10px] font-bold text-[#08757C] uppercase tracking-widest mb-1.5">
                      Alternate Project <span className="text-red-400">*</span>
                    </label>
                    <Dropdown
                      options={(current.ProjectList ?? [])
                        .filter((p) => p.Value !== "")
                        .map((p) => ({
                          value: p.Value,
                          // Show both so an alternate project reads
                          // unambiguously (multiple projects can share a
                          // name) — falls back to just the name for the
                          // "NA"/"Others(Remarks)" placeholder rows, which
                          // have no ProjectCode.
                          label: p.ProjectCode
                            ? `${p.ProjectCode} — ${p.Text}`
                            : p.Text,
                        }))}
                      value={altProjectColumn.ShiftchangeOver_Value || ""}
                      onChange={(v) =>
                        updateValue(
                          altProjectColumn.ShiftchangeOverColumn_ID,
                          v,
                        )
                      }
                      placeholder="Select Alternate Project"
                      disabled={readOnly}
                    />
                    {errors[altProjectColumn.ShiftchangeOverColumn_ID] && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {errors[altProjectColumn.ShiftchangeOverColumn_ID]}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!readOnly && (
            <div className="px-7 pb-6 pt-2 flex gap-3 flex-shrink-0">
              <button
                onClick={onClose}
                className="px-5 h-11 text-[#3e494a] font-semibold text-sm hover:bg-[#f1f4f4] rounded-xl border border-[#e5e9e9] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !current}
                className="flex-1 flex items-center justify-center gap-2 text-white font-bold text-sm rounded-xl hover:brightness-110 transition-all disabled:opacity-60"
                style={{ background: "#08757C", height: "44px" }}
              >
                <span className="material-symbols-outlined text-[17px]">
                  {isUpdate ? "sync" : "save"}
                </span>
                {saving
                  ? isUpdate
                    ? "Updating..."
                    : "Saving..."
                  : isUpdate
                    ? "Update"
                    : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ShiftChangeOverPanel;
